import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BiSolidPhoneCall } from 'react-icons/bi';
import { FaCheck } from 'react-icons/fa6';
import { IoChatbubbleEllipsesOutline } from 'react-icons/io5';
import { MdOutlineSecurity, MdOutlineSwapHoriz } from 'react-icons/md';
import { PiCaretLeftBold, PiCaretRightBold } from 'react-icons/pi';
import { Link, useParams } from 'react-router-dom';
import { ActionIcon, Button, Text, Textarea, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import CustomSelect from '../../../../../components/forms/CustomSelect';
import { Loader } from '../../../../../components/loader/SpinnerLogo';
import ModalForm from '../../../../../components/forms/ModalForm';
import { usePrivilege } from '../../../../../components/priviledge/PriviledgeProvider';
import CustomTable from '../../../../../components/table/CustomTable';
import useFetch from '../../../../../hooks/useFetch';
import useHandleErrors from '../../../../../hooks/useHandleErrors';
import { menuRoutes } from '../../../../../router/menu';
import { CommunicationService } from '../../../../../services/communication/communicationService';
import { DepartmentService } from '../../../../../services/department/departmentService';
import { OrderCampaignService } from '../../../../../services/order/orderCampaignService';
import { OrderService } from '../../../../../services/order/orderService';
import { UserService } from '../../../../../services/user/userService';
import { emitCommunicationAlertsRefresh } from '../../../../../utils/communicationAlerts';
import OrderProfileLayout from '../../OrderProfileLayout';
import CreateOrderCampaignModal from './CreateOrderCampaignModal';

// CORRECCIÓN: Quitamos 'actions' de aquí porque CustomTable la añade automáticamente al pasarle el prop actions
const campaignContractColumnOrder = [
    'code',
    'clientCompanyName',
    'studentName',
    'dinamizador',
    'orderCampaignStage'
];

const stages = [
    { value: 'all', label: 'Todas' },
    { value: 'start', label: 'Inicio' },
    { value: 'follow_up', label: 'Seguimiento' },
    { value: 'end', label: 'Fin' },
    { value: 'completed', label: 'Completado' },
];

const stageLabels: Record<string, string> = {
    start: 'Inicio',
    follow_up: 'Seguimiento',
    end: 'Fin',
    completed: 'Completado',
};

const nextStageByStage: Record<string, string | null> = {
    start: 'follow_up',
    follow_up: 'end',
    end: null,
    completed: null,
};

const previousStageByStage: Record<string, string | null> = {
    start: null,
    follow_up: 'start',
    end: 'follow_up',
    completed: 'end',
};

const getSelectRows = (payload: any): any[] => {
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload)) return payload;
    return [];
};

type FacilitatorOption = {
    value: string;
    label: string;
    email?: string;
    userLogin?: string;
};

type EligibleContractRow = {
    id: string;
    code?: string;
    clientCompanyName?: string;
    studentName?: string;
    studentFirstName?: string;
    studentLastName?: string;
};

const getStudentLabel = (contract: EligibleContractRow): string => {
    return [contract.studentName, contract.studentFirstName, contract.studentLastName]
        .filter(Boolean)
        .join(' ')
        .trim() || 'N/A';
};

const getClientLabel = (contract: EligibleContractRow): string => {
    return contract.clientCompanyName || 'N/A';
};

const OrderCampaigns: React.FC = () => {
    const { id = '' } = useParams<{ id: string }>();
    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const orderCampaignService = new OrderCampaignService();
    const orderService = new OrderService();
    const communicationService = new CommunicationService();
    const userService = new UserService();
    const canCreateCommunications = userCan('create_communications', 'communications');

    const [selectedStage, setSelectedStage] = useState<string>('all');
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
    const [showAddContractModal, setShowAddContractModal] = useState<boolean>(false);
    const [eligibleContracts, setEligibleContracts] = useState<EligibleContractRow[]>([]);
    const [eligibleContractsLoading, setEligibleContractsLoading] = useState<boolean>(false);
    const [selectedContractToAddId, setSelectedContractToAddId] = useState<string>('');
    const [isAddingContract, setIsAddingContract] = useState<boolean>(false);
    const [showCallsModal, setShowCallsModal] = useState<boolean>(false);
    const [selectedContractForCall, setSelectedContractForCall] = useState<any | null>(null);
    const [callsInfo, setCallsInfo] = useState<any>(null);
    const [isMarkCallSaving, setIsMarkCallSaving] = useState<boolean>(false);
    const [callLogForm, setCallLogForm] = useState({
        classificationId: '',
        notes: '',
        scheduledAt: '',
    });
    const [selectedContractForReassign, setSelectedContractForReassign] = useState<any | null>(null);
    const [reassignUserId, setReassignUserId] = useState<string>('');
    const [reassignFacilitators, setReassignFacilitators] = useState<FacilitatorOption[]>([]);
    const [reassignFacilitatorsLoading, setReassignFacilitatorsLoading] = useState<boolean>(false);
    const [reassignSaving, setReassignSaving] = useState<boolean>(false);
    const [hasConfiguredFacilitatorRoles, setHasConfiguredFacilitatorRoles] = useState<boolean>(true);
    const [selectedContractForCommunication, setSelectedContractForCommunication] = useState<any | null>(null);
    const [communicationForm, setCommunicationForm] = useState({
        targetDepartmentId: '',
        message: '',
    });
    const [communicationSaving, setCommunicationSaving] = useState<boolean>(false);

    const [searchCode, setSearchCode] = useState<string>('');
    const [searchClient, setSearchClient] = useState<string>('');

    const getContractProfilePath = (contractId: string) => {
        const basePath = `${menuRoutes.contracts.path}/${contractId}/profile${menuRoutes.contracts.profile.info}`;
        return id
            ? `${basePath}?fromOrderId=${id}&fromOrderView=campaigns`
            : basePath;
    };

    const getStudentProfilePath = (studentId: string) => {
        const basePath = `${menuRoutes.students.path}/${studentId}/profile${menuRoutes.students.profile.info}`;
        return id
            ? `${basePath}?fromOrderId=${id}&fromOrderView=campaigns`
            : basePath;
    };

    const getUserProfilePath = (userId: string) => {
        const basePath = `${menuRoutes.users.path}/${userId}/profile${menuRoutes.users.profile.info}`;
        return id
            ? `${basePath}?fromOrderId=${id}&fromOrderView=campaigns`
            : basePath;
    };

    const [campaignsData, campaignsLoading, _campaignsError, refetchCampaigns] = useFetch(useCallback(async () => {
        if (!id) return null;
        const response = await orderCampaignService.listOrderCampaigns({
            page: 1,
            limit: 50,
            filter_filters: { order_id: id }
        });
        return response.getResponseData();
    }, [id]));

    const campaigns = useMemo(() => {
        if (!campaignsData) return [];
        if (Array.isArray(campaignsData?.data?.data)) return campaignsData.data.data;
        if (Array.isArray(campaignsData?.data)) return campaignsData.data;
        if (Array.isArray(campaignsData)) return campaignsData;
        return [];
    }, [campaignsData]);

    const activeCampaign = useMemo(() => {
        if (!Array.isArray(campaigns) || campaigns.length === 0) return null;
        return campaigns.find((campaign: any) => !campaign.isFinished && !campaign.isCanceled) || null;
    }, [campaigns]);

    const displayedCampaign = activeCampaign || (Array.isArray(campaigns) && campaigns.length > 0 ? campaigns[0] : null);

    const [contractsData, contractsListLoading, _contractsError, refetchContracts] = useFetch(useCallback(async () => {
        if (!displayedCampaign?.id) return null;
        const filterFilters: Record<string, any> = { order_campaign: displayedCampaign.id };

        const response = await orderCampaignService.listOrderCampaignContracts({
            page: 1,
            limit: 99999,
            filter_filters: filterFilters,
        });
        return response.getResponseData();
    }, [displayedCampaign?.id]));

    const [callClassificationResponse, callClassificationLoading] = useFetch(useCallback(async () => {
        if (!selectedContractForCall || !userCan('mark_contract_call_as_realized', 'order_campaigns')) return null;
        const response = await orderCampaignService.listCallClassifications();
        return response.getResponseData();
    }, [selectedContractForCall, userCan]));

    const [communicationDepartmentsResponse] = useFetch(useCallback(async () => {
        if (!canCreateCommunications) return null;
        const departmentService = new DepartmentService();
        const response = await departmentService.listDepartmentsForSelect({
            page: 1,
            limit: 200,
            filter_filters: {
                handles_communications: true,
            },
            filter_order: [{ field: 'name', order: 'asc' }],
        });
        return response.getResponseData();
    }, [canCreateCommunications]));

    useEffect(() => {
        if (
            campaignsData &&
            campaigns.length === 0 &&
            !campaignsLoading &&
            userCan('create_order_campaigns', 'order_campaigns')
        ) {
            setShowCreateModal(true);
        }
    }, [campaignsData, campaigns.length, campaignsLoading, userCan]);

    useEffect(() => {
        if (displayedCampaign?.id) {
            refetchContracts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayedCampaign?.id]);

    const allCampaignContracts = useMemo(() => {
        return Array.isArray(contractsData?.data?.data)
            ? contractsData.data.data
            : (Array.isArray(contractsData?.data) ? contractsData.data : []);
    }, [contractsData]);

    const stageCounts = useMemo(() => {
        const counts: Record<string, number> = { all: allCampaignContracts.length, start: 0, follow_up: 0, end: 0, completed: 0 };
        allCampaignContracts.forEach((contract: any) => {
            const stage = contract.orderCampaignStage;
            if (stage && stage in counts) {
                counts[stage]++;
            }
        });
        return counts;
    }, [allCampaignContracts]);

    const campaignContracts = useMemo(() => {
        let filtered = allCampaignContracts;

        if (selectedStage !== 'all') {
            filtered = filtered.filter((contract: any) => contract.orderCampaignStage === selectedStage);
        }

        if (searchCode.trim() !== '') {
            filtered = filtered.filter((contract: any) =>
                contract.code?.toLowerCase().includes(searchCode.toLowerCase().trim())
            );
        }

        if (searchClient.trim() !== '') {
            filtered = filtered.filter((contract: any) => {
                const searchValue = searchClient.toLowerCase().trim();
                const clientName = (contract.clientCompanyName || '').toLowerCase();
                const studentFullName = [
                    contract.studentName,
                    contract.studentFirstName,
                    contract.studentLastName,
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();

                return clientName.includes(searchValue) || studentFullName.includes(searchValue);
            });
        }

        return filtered;
    }, [allCampaignContracts, selectedStage, searchCode, searchClient]);

    const canCreateCampaigns = userCan('create_order_campaigns', 'order_campaigns');
    const canFinishCampaigns = userCan('finish_order_campaigns', 'order_campaigns');
    const canCancelCampaigns = userCan('cancel_order_campaigns', 'order_campaigns');
    const canManageCampaigns = canCreateCampaigns
        || canFinishCampaigns
        || canCancelCampaigns
        || userCan('change_contract_stage', 'order_campaigns');
    const canReassignContracts = userCan('reassign_contracts_user', 'order_campaigns');
    const displayedCampaignIsClosed = Boolean(displayedCampaign?.isFinished || displayedCampaign?.isCanceled);

    const eligibleContractOptions = useMemo(() => {
        return eligibleContracts.map((contract) => ({
            value: contract.id,
            label: `${contract.code || 'Sin código'} · ${getClientLabel(contract)} · ${getStudentLabel(contract)}`,
        }));
    }, [eligibleContracts]);

    const handleStageInfo = async (stage: string) => {
        if (!displayedCampaign?.id || stage === 'all') return;
        const response = await (await orderCampaignService.getStageCalls(displayedCampaign.id, stage)).getResponseData();
        if (response.success) {
            setCallsInfo(response.data);
            setShowCallsModal(true);
        } else {
            handleErrors(response);
        }
    };

    const updateContractStage = async (contractId: string, stage: string) => {
        if (!displayedCampaign?.id) return;
        const response = await (await orderCampaignService.changeContractStage(displayedCampaign.id, contractId, stage)).getResponseData();
        if (response.success) {
            toast.success('Etapa actualizada');
            refetchContracts();
        } else {
            handleErrors(response);
        }
    };

    const openMarkCallModal = (contract: any) => {
        setSelectedContractForCall(contract);
        setCallLogForm({
            classificationId: '',
            notes: '',
            scheduledAt: '',
        });
    };

    const closeMarkCallModal = () => {
        setSelectedContractForCall(null);
        setCallLogForm({
            classificationId: '',
            notes: '',
            scheduledAt: '',
        });
    };

    const loadReassignFacilitators = async () => {
        setReassignFacilitatorsLoading(true);
        try {
            const companyConfigResponse = await userService.getCompanyConfigurationInfo();
            const companyConfig = companyConfigResponse.getResponseData()?.data;
            const facilitatorRoleIds = Array.isArray(companyConfig?.facilitatorRoleIds)
                ? companyConfig.facilitatorRoleIds.filter(Boolean)
                : [];

            if (facilitatorRoleIds.length === 0) {
                setHasConfiguredFacilitatorRoles(false);
                setReassignFacilitators([]);
                return;
            }

            setHasConfiguredFacilitatorRoles(true);

            const response = await userService.listUsersForSelect({
                limit: 999999,
                filter_filters: {
                    roles: facilitatorRoleIds,
                },
            });

            const users = getSelectRows(response.getResponseData());
            setReassignFacilitators(users.map((user: any) => ({
                value: user.id,
                label: [user.name, user.lastName].filter(Boolean).join(' ').trim() || user.email || 'Sin nombre',
                email: user.email || '',
                userLogin: user.userLogin || '',
            })));
        } catch (error: any) {
            toast.error(error?.message || 'No se pudieron cargar los dinamizadores');
        } finally {
            setReassignFacilitatorsLoading(false);
        }
    };

    const openReassignModal = async (contract: any) => {
        if (contract?.orderCampaignSecured) {
            toast.error('Los contratos blindados no permiten reasignar el dinamizador');
            return;
        }

        setSelectedContractForReassign(contract);
        setReassignUserId(contract.orderCampaignAssignedUserId || '');
        await loadReassignFacilitators();
    };

    const closeAddContractModal = () => {
        setShowAddContractModal(false);
        setEligibleContracts([]);
        setEligibleContractsLoading(false);
        setSelectedContractToAddId('');
        setIsAddingContract(false);
    };

    const loadEligibleContracts = async () => {
        if (!id || !activeCampaign?.id) return;

        setEligibleContractsLoading(true);

        try {
            const response = await orderService.getOrderContracts({
                page: 1,
                limit: 99999,
                filter_filters: {
                    order: id,
                    exclude_secured_order_campaign_contracts: true,
                },
            });

            const currentCampaignContractIds = new Set(allCampaignContracts.map((contract: any) => contract.id));
            const availableContracts = getSelectRows(response.getResponseData()).filter((contract: EligibleContractRow) =>
                contract?.id && !currentCampaignContractIds.has(contract.id)
            );

            setEligibleContracts(availableContracts);
            setSelectedContractToAddId('');
        } catch (error: any) {
            toast.error(error?.message || 'No se pudieron cargar los contratos elegibles');
        } finally {
            setEligibleContractsLoading(false);
        }
    };

    const openAddContractModal = async () => {
        if (!activeCampaign?.id) return;

        setShowAddContractModal(true);
        await loadEligibleContracts();
    };

    const closeReassignModal = () => {
        setSelectedContractForReassign(null);
        setReassignUserId('');
        setReassignFacilitators([]);
        setReassignFacilitatorsLoading(false);
        setReassignSaving(false);
        setHasConfiguredFacilitatorRoles(true);
    };

    const handleAddContractToCampaign = async () => {
        if (!activeCampaign?.id || !selectedContractToAddId) {
            toast.error('Debes seleccionar un contrato.');
            return;
        }

        setIsAddingContract(true);

        try {
            const response = await (
                await orderCampaignService.addContract(activeCampaign.id, selectedContractToAddId)
            ).getResponseData();

            if (response.success) {
                toast.success('Contrato añadido a la campaña');
                closeAddContractModal();
                refetchContracts();
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        } finally {
            setIsAddingContract(false);
        }
    };

    const openCommunicationModal = (contract: any) => {
        setSelectedContractForCommunication(contract);
        setCommunicationForm({
            targetDepartmentId: communicationDepartments[0]?.id || '',
            message: '',
        });
    };

    const closeCommunicationModal = () => {
        setSelectedContractForCommunication(null);
        setCommunicationForm({
            targetDepartmentId: communicationDepartments[0]?.id || '',
            message: '',
        });
        setCommunicationSaving(false);
    };

    const markCall = async () => {
        if (!displayedCampaign?.id || !selectedContractForCall?.id) return;
        if (!callLogForm.classificationId) {
            toast.error('Debes seleccionar una tipificación.');
            return;
        }

        if (selectedCallClassification?.requiresSchedule && !callLogForm.scheduledAt) {
            toast.error('Debes indicar la fecha y hora de agenda.');
            return;
        }

        setIsMarkCallSaving(true);

        try {
            const response = await (
                await orderCampaignService.markContractCallAsRealized(
                    displayedCampaign.id,
                    selectedContractForCall.id,
                    true,
                    callLogForm.classificationId,
                    callLogForm.notes.trim(),
                    selectedCallClassification?.requiresSchedule ? callLogForm.scheduledAt : null,
                )
            ).getResponseData();

            if (response.success) {
                toast.success('Llamada marcada y seguimiento registrado');
                closeMarkCallModal();
                refetchContracts();
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        } finally {
            setIsMarkCallSaving(false);
        }
    };

    const handleReassignContract = async () => {
        if (!displayedCampaign?.id || !selectedContractForReassign?.id || !reassignUserId) {
            toast.error('Debes seleccionar un dinamizador.');
            return;
        }

        if (selectedContractForReassign?.orderCampaignSecured) {
            toast.error('Los contratos blindados no permiten reasignar el dinamizador');
            return;
        }

        setReassignSaving(true);

        try {
            const response = await (
                await orderCampaignService.reassignContractsUser(
                    displayedCampaign.id,
                    [selectedContractForReassign.id],
                    reassignUserId
                )
            ).getResponseData();

            if (response.success) {
                toast.success('Dinamizador reasignado');
                closeReassignModal();
                refetchContracts();
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        } finally {
            setReassignSaving(false);
        }
    };

    const handleCreateCommunication = async () => {
        if (!selectedContractForCommunication?.id) return;
        if (!communicationForm.message.trim()) {
            toast.error('Debes indicar el mensaje inicial.');
            return;
        }

        setCommunicationSaving(true);

        try {
            const response = await (await communicationService.createCommunication({
                contractId: selectedContractForCommunication.id,
                targetDepartmentId: communicationForm.targetDepartmentId,
                message: communicationForm.message.trim(),
            })).getResponseData();

            if (response?.success) {
                toast.success('Comunicación registrada correctamente');
                closeCommunicationModal();
                emitCommunicationAlertsRefresh();
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        } finally {
            setCommunicationSaving(false);
        }
    };

    const secureContract = async (contractId: string) => {
        if (!displayedCampaign?.id) return;
        const response = await (await orderCampaignService.secureContracts(displayedCampaign.id, [contractId])).getResponseData();
        if (response.success) {
            toast.success('Contrato blindado');
            refetchContracts();
        } else {
            handleErrors(response);
        }
    };

    const finishCampaign = async () => {
        if (!activeCampaign?.id) return;
        const response = await (await orderCampaignService.finishOrderCampaign(activeCampaign.id)).getResponseData();
        if (response.success) {
            toast.success('Campaña finalizada');
            refetchCampaigns();
            refetchContracts();
        } else {
            handleErrors(response);
        }
    };

    const cancelCampaign = async () => {
        if (!activeCampaign?.id) return;
        const response = await (await orderCampaignService.cancelOrderCampaign(activeCampaign.id)).getResponseData();
        if (response.success) {
            toast.success('Campaña cancelada');
            refetchCampaigns();
            refetchContracts();
        } else {
            handleErrors(response);
        }
    };

    const handleResetFilters = () => {
        setSearchCode('');
        setSearchClient('');
    };

    const mockTableFilters = useMemo(() => ({
        page: 1,
        limit: 50,
        filter_order: [],
        filter_filters: {}
    }), []);

    const callClassifications = getSelectRows(callClassificationResponse);
    const communicationDepartments = getSelectRows(communicationDepartmentsResponse);
    const callClassificationOptions = callClassifications.map((classification: any) => ({
        value: classification.id,
        label: classification.name,
        requiresSchedule: Boolean(classification.requiresSchedule),
    }));
    const selectedCallClassification = callClassificationOptions.find((classification) => classification.value === callLogForm.classificationId) || null;

    useEffect(() => {
        if (communicationDepartments.length === 0 || !selectedContractForCommunication) {
            return;
        }

        setCommunicationForm((current) => {
            if (current.targetDepartmentId) {
                return current;
            }

            return {
                ...current,
                targetDepartmentId: communicationDepartments[0].id,
            };
        });
    }, [communicationDepartments, selectedContractForCommunication]);

    return (
        <OrderProfileLayout>
            {campaignsLoading ? (
                <Loader height='50vh' />
            ) : (
                <div className='space-y-4 pt-4'>
                    {displayedCampaign && (
                        <div className='flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4'>
                            <div className='flex flex-col gap-2 md:flex-row md:items-center md:justify-between'>
                                <div>
                                    <Text className='text-lg font-semibold text-gray-900'>{displayedCampaign.name}</Text>
                                    <Text className='text-sm text-gray-500'>{displayedCampaign.description || 'Sin descripción'}</Text>
                                </div>
                                {canManageCampaigns && (
                                    <div className='flex gap-2'>
                                        {activeCampaign && canFinishCampaigns && <Button variant='outline' onClick={finishCampaign}>Finalizar</Button>}
                                        {activeCampaign && canCancelCampaigns && <Button color='danger' variant='outline' onClick={cancelCampaign}>Cancelar</Button>}
                                        {activeCampaign && canCreateCampaigns && <Button variant='outline' onClick={openAddContractModal}>Añadir contrato</Button>}
                                        {!activeCampaign && canCreateCampaigns && <Button onClick={() => setShowCreateModal(true)}>Crear campaña</Button>}
                                    </div>
                                )}
                            </div>

                            <div className='flex flex-wrap gap-2'>
                                {stages.map((stage) => (
                                    <Button
                                        key={stage.value}
                                        variant={selectedStage === stage.value ? 'solid' : 'outline'}
                                        color={selectedStage === stage.value ? 'primary' : 'secondary'}
                                        onClick={() => setSelectedStage(stage.value)}
                                        size='sm'
                                    >
                                        {stage.label} ({stageCounts[stage.value] || 0})
                                    </Button>
                                ))}
                                {selectedStage !== 'all' && (
                                    <Button variant='outline' size='sm' onClick={() => handleStageInfo(selectedStage)}>
                                        Ver información de llamadas
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                    {displayedCampaign && (
                        <div className='flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
                            <div className='w-full sm:w-64'>
                                <label className='block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5'>Contrato</label>
                                <input
                                    type='text'
                                    className='w-full h-9 rounded-md border border-gray-300 px-3 text-sm placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                                    placeholder='Buscar por código...'
                                    value={searchCode}
                                    onChange={(e) => setSearchCode(e.target.value)}
                                />
                            </div>
                            <div className='w-full sm:w-80'>
                                <label className='block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5'>Cliente / Alumno</label>
                                <input
                                    type='text'
                                    className='w-full h-9 rounded-md border border-gray-300 px-3 text-sm placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                                    placeholder='Buscar por nombre o empresa...'
                                    value={searchClient}
                                    onChange={(e) => setSearchClient(e.target.value)}
                                />
                            </div>
                            {(searchCode || searchClient) && (
                                <Button
                                    variant='outline'
                                    size='sm'
                                    onClick={handleResetFilters}
                                    className='h-9 text-xs font-medium text-gray-600 hover:text-gray-900'
                                >
                                    Limpiar filtros
                                </Button>
                            )}
                        </div>
                    )}

                    <div className='bg-white rounded-lg'>
                        <CustomTable
                            largeTable
                            id={'campaign-contracts-table'}
                            columnOrder={campaignContractColumnOrder}
                            data={campaignContracts}
                            isLoading={contractsListLoading}
                            columnsNotShown={[
                                'id',
                                'orderCampaignContractId',
                                'orderCampaignId',
                                'studentId',
                                'studentFirstName',
                                'studentLastName',
                                'orderCampaignStartCallRealized',
                                'orderCampaignFollowUpCallRealized',
                                'orderCampaignEndCallRealized',
                                'orderCampaignSecured',
                                'orderCampaignSecuredAt',
                                'createdAt',
                                'updatedAt',
                                'orderCampaignAssignedUserId',
                                'orderCampaignAssignedUserLastName'
                            ]}
                            overrideColumns={[
                                {
                                    key: 'code',
                                    render: (row: any) => {
                                        const contract = row.row.original;
                                        return userCan('get_contracts', 'contracts') ? (
                                            <Link to={getContractProfilePath(contract.id)}>
                                                <Text className='font-bold text-primary'>{contract.code}</Text>
                                            </Link>
                                        ) : (
                                            <Text className='font-bold text-primary'>{contract.code}</Text>
                                        );
                                    }
                                },
                                {
                                    key: 'clientCompanyName',
                                    render: (row: any) => {
                                        const contract = row.row.original;
                                        const clientName = contract.clientCompanyName || 'N/A';

                                        return contract.clientId && userCan('get_clients', 'clients') ? (
                                            <Link to={`${menuRoutes.clients.path}/${contract.clientId}/profile/info`} className='font-bold text-primary cursor-pointer block'>
                                                {clientName}
                                            </Link>
                                        ) : (
                                            <span className='font-semibold text-gray-700'>{clientName}</span>
                                        );
                                    }
                                },
                                {
                                    key: 'studentName',
                                    render: (row: any) => {
                                        const contract = row.row.original;
                                        const fullName = [contract.studentName, contract.studentFirstName, contract.studentLastName].filter(Boolean).join(' ').trim() || contract.studentName || 'N/A';
                                        return contract.studentId && userCan('get_students', 'students') ? (
                                            <Link to={getStudentProfilePath(contract.studentId)}>
                                                <Text className='font-bold text-primary'>{fullName}</Text>
                                            </Link>
                                        ) : (
                                            <Text className='font-bold text-primary'>{fullName}</Text>
                                        );
                                    }
                                },
                                {
                                    // CORRECCIÓN: 'dinamizador' en minúsculas para que coincida exactamente con el columnOrder
                                    key: 'dinamizador',
                                    render: (row: any) => {
                                        const contract = row.row.original;
                                        const facilitatorName = [contract.orderCampaignAssignedUserName, contract.orderCampaignAssignedUserLastName].filter(Boolean).join(' ').trim();
                                        return (
                                            <span className='text-gray-700 font-medium'>
                                                {facilitatorName || 'Sin asignar'}
                                            </span>
                                        );
                                    }
                                },
                                {
                                    key: 'orderCampaignStage',
                                    render: (row: any) => {
                                        const contract = row.row.original;
                                        return (
                                            <Text className='font-medium text-gray-600'>
                                                {stageLabels[contract.orderCampaignStage] || contract.orderCampaignStage || 'N/A'}
                                            </Text>
                                        );
                                    }
                                },
                                {
                                    key: 'orderCampaignAssignedUserName',
                                    render: (row: any) => {
                                        const contract = row.row.original;
                                        const fullName = [contract.orderCampaignAssignedUserName, contract.orderCampaignAssignedUserLastName].filter(Boolean).join(' ').trim() || contract.orderCampaignAssignedUserName || 'N/A';
                                        return contract.orderCampaignAssignedUserId && userCan('get_user', 'user') ? (
                                            <Link to={getUserProfilePath(contract.orderCampaignAssignedUserId)}>
                                                <Text className='font-bold text-primary'>{fullName}</Text>
                                            </Link>
                                        ) : (
                                            <Text className='font-bold text-primary'>{fullName}</Text>
                                        );
                                    }
                                }
                            ]}
                            // CORRECCIÓN: Pasamos las acciones mediante el prop nativo del CustomTable del proyecto.
                            // Nota: En este array, la función 'render' recibe directamente el registro (contract) como parámetro.
                            actions={[
                                {
                                    label: 'Acciones de campaña',
                                    render: (contract: any) => {
                                        const nextStage = previousStageByStage[contract.orderCampaignStage];
                                        const forwardStage = nextStageByStage[contract.orderCampaignStage];
                                        const isSecured = Boolean(contract.orderCampaignSecured);
                                        const canShowReassign = !displayedCampaignIsClosed
                                            && canReassignContracts
                                            && !isSecured;

                                        const isCallRealized = (() => {
                                            switch (contract.orderCampaignStage) {
                                                case 'start':
                                                    return Boolean(contract.orderCampaignStartCallRealized);
                                                case 'follow_up':
                                                    return Boolean(contract.orderCampaignFollowUpCallRealized);
                                                case 'end':
                                                    return Boolean(contract.orderCampaignEndCallRealized);
                                                case 'completed':
                                                    return true;
                                                default:
                                                    return false;
                                            }
                                        })();

                                        return (
                                            <div className='flex flex-wrap gap-2 items-center'>
                                                {isCallRealized ? (
                                                    <Tooltip size="sm" content="Llamada realizada" placement="top" color="invert">
                                                        <ActionIcon
                                                            as="span"
                                                            size="sm"
                                                            variant="outline"
                                                            className="cursor-default border-green-200 bg-green-50 text-green-700 hover:!border-green-200 hover:bg-green-50 hover:text-green-700"
                                                        >
                                                            <FaCheck className="h-3.5 w-3.5" />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                ) : (
                                                    !displayedCampaignIsClosed && !isSecured && userCan('mark_contract_call_as_realized', 'order_campaigns') && contract.orderCampaignStage !== 'completed' && (
                                                        <Tooltip size="sm" content="Marcar llamada como realizada" placement="top" color="invert">
                                                            <ActionIcon
                                                                as="button"
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => openMarkCallModal(contract)}
                                                                className="border-green-200 text-green-700 hover:!border-green-500 hover:bg-green-50 hover:text-green-800"
                                                            >
                                                                <BiSolidPhoneCall className="h-4 w-4" size={18} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                    )
                                                )}
                                                {!displayedCampaignIsClosed && !isSecured && nextStage && userCan('change_contract_stage', 'order_campaigns') && (
                                                    <Tooltip size="sm" content="Retroceder etapa" placement="top" color="invert">
                                                        <ActionIcon
                                                            as="button"
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => updateContractStage(contract.id, nextStage)}
                                                            className="border-amber-200 text-amber-700 hover:!border-amber-500 hover:bg-amber-50 hover:text-amber-800"
                                                        >
                                                            <PiCaretLeftBold className="h-4 w-4" />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                )}
                                                {!displayedCampaignIsClosed && !isSecured && forwardStage && userCan('change_contract_stage', 'order_campaigns') && (
                                                    <Tooltip
                                                        size="sm"
                                                        content={isCallRealized ? 'Avanzar etapa' : 'Debes marcar la llamada como realizada antes de avanzar'}
                                                        placement="top"
                                                        color={isCallRealized ? 'invert' : 'danger'}
                                                    >
                                                        <span>
                                                            <ActionIcon
                                                                as="button"
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => updateContractStage(contract.id, forwardStage)}
                                                                disabled={!isCallRealized}
                                                                className="border-sky-200 text-sky-700 hover:!border-sky-500 hover:bg-sky-50 hover:text-sky-800 disabled:border-gray-200 disabled:text-gray-400 disabled:hover:bg-transparent"
                                                            >
                                                                <PiCaretRightBold className="h-4 w-4" />
                                                            </ActionIcon>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                                {!displayedCampaignIsClosed && !isSecured && contract.orderCampaignStage === 'end' && userCan('secure_campaign_contracts', 'order_campaigns') && (
                                                    <Tooltip size="sm" content="Blindar" placement="top" color="invert">
                                                        <ActionIcon
                                                            as="button"
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => secureContract(contract.id)}
                                                            className="border-violet-200 text-violet-700 hover:!border-violet-500 hover:bg-violet-50 hover:text-violet-800"
                                                        >
                                                            <MdOutlineSecurity className="h-4 w-4" />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                )}
                                                {isSecured && (
                                                    <span className='inline-flex items-center rounded-md border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-500 bg-gray-50'>
                                                        Blindado
                                                    </span>
                                                )}
                                                {canShowReassign && (
                                                    <Tooltip size="sm" content="Reasignar dinamizador" placement="top" color="invert">
                                                        <ActionIcon
                                                            as="button"
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => openReassignModal(contract)}
                                                            className="border-indigo-200 text-indigo-700 hover:!border-indigo-500 hover:bg-indigo-50 hover:text-indigo-800"
                                                        >
                                                            <MdOutlineSwapHoriz className="h-4 w-4" />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                )}
                                                {canCreateCommunications && (
                                                    <Tooltip size="sm" content="Crear comunicación" placement="top" color="invert">
                                                        <ActionIcon
                                                            as="button"
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => openCommunicationModal(contract)}
                                                            className="border-cyan-200 text-cyan-700 hover:!border-cyan-500 hover:bg-cyan-50 hover:text-cyan-800"
                                                        >
                                                            <IoChatbubbleEllipsesOutline className="h-4 w-4" />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        );
                                    }
                                }
                            ]}
                            paginationData={{
                                pageSize: campaignContracts.length,
                                currentPage: 1,
                                pageCount: 1,
                                totalCount: campaignContracts.length,
                                handlePagination: () => {},
                                handlePerPage: () => {},
                            }}
                            filters={mockTableFilters}
                            updateFilters={() => {}}
                            updateFilterOrder={() => {}}
                            defaultOrder={[]}
                        />
                    </div>
                </div>
            )}

            {id && (
                <CreateOrderCampaignModal
                    isOpen={showCreateModal && userCan('create_order_campaigns', 'order_campaigns')}
                    orderId={id}
                    onClose={() => setShowCreateModal(false)}
                    onCreated={() => {
                        refetchCampaigns();
                        refetchContracts();
                    }}
                />
            )}

            <ModalForm isOpen={showCallsModal} onClose={() => setShowCallsModal(false)} title='Información de llamadas'>
                <div className='space-y-2 pb-4 text-sm text-gray-700'>
                    <div>Total: {callsInfo?.total || 0}</div>
                    <div>Realizadas: {callsInfo?.realized || 0}</div>
                    <div>Pendientes: {callsInfo?.pending || 0}</div>
                </div>
            </ModalForm>

            <ModalForm
                isOpen={showAddContractModal}
                onClose={closeAddContractModal}
                title='Añadir contrato'
                size='lg'
            >
                <div className='space-y-4'>
                    {eligibleContractsLoading ? (
                        <Text className='text-sm text-gray-600'>Cargando contratos elegibles...</Text>
                    ) : (
                        <>
                            <CustomSelect
                                id='addContractToCampaign'
                                label='Contrato'
                                required
                                isSearchable
                                isClearable
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                menuPosition='fixed'
                                options={eligibleContractOptions}
                                value={eligibleContractOptions.find((option) => option.value === selectedContractToAddId) || null}
                                onChange={(selected: any) => setSelectedContractToAddId(selected?.value || '')}
                                noOption={() => 'No hay contratos elegibles para añadir'}
                                placeholder='contrato'
                            />

                            {eligibleContracts.length === 0 && (
                                <Text className='text-sm text-gray-600'>
                                    No hay contratos del pedido disponibles para añadir a la campaña activa.
                                </Text>
                            )}
                        </>
                    )}

                    <div className='flex justify-end gap-2'>
                        <Button variant='outline' onClick={closeAddContractModal} disabled={isAddingContract}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleAddContractToCampaign}
                            disabled={eligibleContractsLoading || isAddingContract || !selectedContractToAddId}
                        >
                            {isAddingContract ? 'Añadiendo...' : 'Añadir contrato'}
                        </Button>
                    </div>
                </div>
            </ModalForm>

            <ModalForm
                isOpen={selectedContractForCall !== null}
                onClose={closeMarkCallModal}
                title={`Registrar llamada${selectedContractForCall?.code ? ` · ${selectedContractForCall.code}` : ''}`}
                size='lg'
            >
                <div className='space-y-4'>
                    <CustomSelect
                        id='campaignCallClassification'
                        label='Tipificación'
                        required
                        isSearchable
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        menuPosition='fixed'
                        isLoading={callClassificationLoading}
                        options={callClassificationOptions}
                        value={selectedCallClassification}
                        onChange={(selected: any) => setCallLogForm((current) => ({
                            ...current,
                            classificationId: selected?.value || '',
                            scheduledAt: selected?.requiresSchedule ? current.scheduledAt : '',
                        }))}
                    />
                    {callClassificationResponse?.success === false && (
                        <Text className='text-sm text-red-600'>
                            {callClassificationResponse?.message || 'No se han podido cargar las tipificaciones.'}
                        </Text>
                    )}

                    {selectedCallClassification?.requiresSchedule && (
                        <div>
                            <label className='mb-1.5 block text-sm font-medium text-gray-700'>Fecha y hora de agenda</label>
                            <input
                                className='h-10 w-full rounded-md border border-gray-300 px-3 text-sm'
                                type='datetime-local'
                                value={callLogForm.scheduledAt}
                                onChange={(event) => setCallLogForm((current) => ({ ...current, scheduledAt: event.target.value }))}
                            />
                        </div>
                    )}

                    <div>
                        <label className='mb-1.5 block text-sm font-medium text-gray-700'>Observaciones</label>
                        <textarea
                            className='min-h-[140px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm'
                            value={callLogForm.notes}
                            onChange={(event) => setCallLogForm((current) => ({ ...current, notes: event.target.value }))}
                            placeholder='Añade el detalle del seguimiento...'
                        />
                    </div>

                    <div className='flex justify-end gap-2'>
                        <Button variant='outline' onClick={closeMarkCallModal} disabled={isMarkCallSaving}>
                            Cancelar
                        </Button>
                        <Button onClick={markCall} disabled={isMarkCallSaving || !callLogForm.classificationId}>
                            {isMarkCallSaving ? 'Guardando...' : 'Guardar llamada'}
                        </Button>
                    </div>
                </div>
            </ModalForm>

            <ModalForm
                isOpen={selectedContractForReassign !== null}
                onClose={closeReassignModal}
                title={`Reasignar dinamizador${selectedContractForReassign?.code ? ` · ${selectedContractForReassign.code}` : ''}`}
                size='md'
            >
                <div className='space-y-4'>
                    {!hasConfiguredFacilitatorRoles ? (
                        <Text className='text-sm text-red-600'>
                            La empresa no tiene configurados los roles de dinamizador.
                        </Text>
                    ) : (
                        <CustomSelect
                            id='campaignReassignFacilitator'
                            label='Dinamizador'
                            required
                            isSearchable
                            isLoading={reassignFacilitatorsLoading}
                            options={reassignFacilitators}
                            value={reassignFacilitators.find((option) => option.value === reassignUserId) || null}
                            onChange={(selected: any) => setReassignUserId(selected?.value || '')}
                        />
                    )}

                    <div className='flex justify-end gap-2'>
                        <Button variant='outline' onClick={closeReassignModal} disabled={reassignSaving}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleReassignContract}
                            disabled={reassignSaving || reassignFacilitatorsLoading || !hasConfiguredFacilitatorRoles || !reassignUserId}
                        >
                            {reassignSaving ? 'Guardando...' : 'Reasignar'}
                        </Button>
                    </div>
                </div>
            </ModalForm>

            <ModalForm
                isOpen={selectedContractForCommunication !== null}
                onClose={closeCommunicationModal}
                title={`Nueva comunicación${selectedContractForCommunication?.code ? ` · ${selectedContractForCommunication.code}` : ''}`}
                size='lg'
            >
                <div className='space-y-4'>
                    <div>
                        <label className='mb-1.5 block text-sm font-medium text-gray-700'>Departamento</label>
                        <select
                            className='w-full rounded-md border border-gray-300 px-3 py-2 text-sm'
                            value={communicationForm.targetDepartmentId}
                            onChange={(event) => setCommunicationForm((current) => ({ ...current, targetDepartmentId: event.target.value }))}
                        >
                            {communicationDepartments.length === 0 && (
                                <option value=''>No hay departamentos disponibles</option>
                            )}
                            {communicationDepartments.map((department: any) => (
                                <option key={department.id} value={department.id}>
                                    {department.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {communicationDepartments.length === 0 && (
                        <Text className='text-sm text-amber-700'>
                            No hay departamentos configurados para atender comunicaciones.
                        </Text>
                    )}

                    <Textarea
                        label='Mensaje inicial'
                        value={communicationForm.message}
                        onChange={(event) => setCommunicationForm((current) => ({ ...current, message: event.target.value }))}
                        placeholder='Mensaje inicial para el departamento'
                        rows={7}
                    />

                    <div className='flex justify-end gap-2'>
                        <Button variant='outline' onClick={closeCommunicationModal} disabled={communicationSaving}>
                            Cancelar
                        </Button>
                        <Button onClick={handleCreateCommunication} disabled={communicationSaving || !communicationForm.message.trim() || !communicationForm.targetDepartmentId}>
                            {communicationSaving ? 'Guardando...' : 'Crear comunicación'}
                        </Button>
                    </div>
                </div>
            </ModalForm>
        </OrderProfileLayout>
    );
};

export default OrderCampaigns;
